import { useState, useEffect, useCallback } from "react";
import { 
  getMilkEntries, 
  getCattleFoodEntries,
  getFarmerPayments,
  getTotalPaymentsForFarmer
} from "../services/firebaseService";

function getToday() {
  return new Date().toISOString().slice(0, 10);
}

export default function FarmerDashboard() {
  const [entries, setEntries] = useState([]);
  const [cattleFoodEntries, setCattleFoodEntries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('milk'); // 'milk', 'cattleFood', 'payments'
  
  // Get farmer info from localStorage
  const farmerName = localStorage.getItem('farmerName');
  const farmerCode = localStorage.getItem('farmerCode');
  const farmerMobile = localStorage.getItem('farmerMobile') || 'N/A';
  const farmerVillage = localStorage.getItem('farmerVillage') || 'N/A';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Clear cache to ensure fresh data for the logged-in farmer
      const loginMode = localStorage.getItem('loginMode');
      if (loginMode === 'farmer') {
        // Clear all caches to force fresh data fetch
        localStorage.removeItem('farmersCache');
        localStorage.removeItem('entriesCache');
        localStorage.removeItem('cattleFoodEntriesCache');
        localStorage.removeItem('paymentsCache');
      }
      
      const [entriesData, cattleFoodData, paymentsData] = await Promise.all([
        getMilkEntries(false), // Don't use cache
        getCattleFoodEntries(false), // Don't use cache
        getFarmerPayments(false) // Don't use cache
      ]);
      
      console.log('Loaded data for farmer:', {
        entries: entriesData.length,
        cattleFood: cattleFoodData.length,
        payments: paymentsData.length
      });
      
      setEntries(entriesData);
      setCattleFoodEntries(cattleFoodData);
      setPayments(paymentsData);
    } catch (error) {
      console.error('Error loading farmer data:', error);
      alert('Error loading data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter entries for selected date
  const selectedDateEntries = entries.filter(e => e.date === selectedDate);
  const selectedDateCattleFood = cattleFoodEntries.filter(e => e.date === selectedDate);
  
  // Calculate totals for selected date
  const totalQuantity = selectedDateEntries.reduce((sum, e) => sum + (e.quantity || 0), 0);
  const totalAmount = selectedDateEntries.reduce((sum, e) => sum + ((e.quantity || 0) * (e.price || 0)), 0);
  const cattleFoodTotal = selectedDateCattleFood.reduce((sum, e) => sum + (e.totalAmount || 0), 0);
  const netAmount = totalAmount - cattleFoodTotal;
  
  // Get total payments received
  const totalPayments = getTotalPaymentsForFarmer(payments, farmerCode);

  // Get recent entries (last 10)
  const recentEntries = entries.slice(0, 10);

  return (
    <div className="container fade-in">
      {/* Farmer Profile Card */}
      <div className="card">
        <div className="farmer-profile">
          <div className="farmer-header">
            <h2>👨‍🌾 {farmerName}</h2>
            <div className="farmer-details">
              <span><strong>Code:</strong> {farmerCode}</span>
              <span><strong>Mobile:</strong> {farmerMobile}</span>
              <span><strong>Village:</strong> {farmerVillage}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <div 
          className={`action-card ${activeView === 'milk' ? 'active' : ''}`}
          onClick={() => setActiveView('milk')}
        >
          <span className="action-icon">🥛</span>
          <span className="action-label">Milk Entries</span>
          <span className="action-count">{entries.length}</span>
        </div>
        <div 
          className={`action-card ${activeView === 'cattleFood' ? 'active' : ''}`}
          onClick={() => setActiveView('cattleFood')}
        >
          <span className="action-icon">🌾</span>
          <span className="action-label">Cattle Food</span>
          <span className="action-count">{cattleFoodEntries.length}</span>
        </div>
        <div 
          className={`action-card ${activeView === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveView('payments')}
        >
          <span className="action-icon">💳</span>
          <span className="action-label">Total Payments</span>
          <span className="action-count">₹{totalPayments}</span>
        </div>
      </div>

      {/* Date Selection - Only show for milk and cattle food views */}
      {(activeView === 'milk' || activeView === 'cattleFood') && (
        <div className="card">
          <div className="date-selector">
            <label htmlFor="date-select">Select Date:</label>
            <input 
              id="date-select"
              type="date" 
              value={selectedDate} 
              onChange={(e) => setSelectedDate(e.target.value)}
              className="form--input"
              style={{ maxWidth: 200 }}
            />
          </div>
        </div>
      )}

      {/* Daily Summary - Only show for milk view */}
      {activeView === 'milk' && (
        <div className="summary-grid">
          <div className="summary-card">
            <span>📅 Date:</span>
            <b>{new Date(selectedDate).toLocaleDateString('en-IN', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'short' 
            })}</b>
          </div>
          <div className="summary-card">
            <span>🥛 Total Quantity:</span>
            <b>{totalQuantity.toFixed(1)} L</b>
          </div>
          <div className="summary-card">
            <span>💰 Milk Amount:</span>
            <b>₹{totalAmount.toFixed(2)}</b>
          </div>
          <div className="summary-card">
            <span>🌾 Cattle Food:</span>
            <b>₹{cattleFoodTotal.toFixed(2)}</b>
          </div>
          <div className="summary-card">
            <span>💵 Net Amount:</span>
            <b style={{ color: netAmount >= 0 ? 'green' : 'red' }}>
              ₹{netAmount.toFixed(2)}
            </b>
          </div>
        </div>
      )}

      {/* Content based on active view */}
      {activeView === 'milk' && (
        <>
          {/* Daily Entries Table */}
          <div className="card">
            <div className="table-header">
              <h3>Milk Entries for {new Date(selectedDate).toLocaleDateString('en-IN')}</h3>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Animal</th>
                    <th>Time</th>
                    <th>Qty (L)</th>
                    <th>Fat %</th>
                    <th>Price (₹/L)</th>
                    <th>Total (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="table-loading">
                        <div className="mini-spinner"></div>Loading entries...
                      </td>
                    </tr>
                  ) : selectedDateEntries.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="empty-state">
                        No entries for this date.
                      </td>
                    </tr>
                  ) : (
                    selectedDateEntries.map((entry, index) => (
                      <tr key={`${entry.id}-${index}`}>
                        <td>{entry.animalType}</td>
                        <td>{entry.time}</td>
                        <td>{entry.quantity}</td>
                        <td>{entry.fat?.toFixed(1) || '-'}</td>
                        <td>₹{entry.price}</td>
                        <td>₹{((entry.quantity || 0) * (entry.price || 0)).toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Entries */}
          <div className="card">
            <div className="table-header">
              <h3>Recent Milk Entries</h3>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Animal</th>
                    <th>Time</th>
                    <th>Qty (L)</th>
                    <th>Fat %</th>
                    <th>Total (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="table-loading">
                        <div className="mini-spinner"></div>Loading entries...
                      </td>
                    </tr>
                  ) : recentEntries.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="empty-state">
                        No entries found.
                      </td>
                    </tr>
                  ) : (
                    recentEntries.map((entry, index) => (
                      <tr key={`recent-${entry.id}-${index}`}>
                        <td>{new Date(entry.date).toLocaleDateString('en-IN', { 
                          day: 'numeric', 
                          month: 'short' 
                        })}</td>
                        <td>{entry.animalType}</td>
                        <td>{entry.time}</td>
                        <td>{entry.quantity}</td>
                        <td>{entry.fat?.toFixed(1) || '-'}</td>
                        <td>₹{((entry.quantity || 0) * (entry.price || 0)).toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeView === 'cattleFood' && (
        <>
          {/* Daily Cattle Food Summary */}
          <div className="summary-grid">
            <div className="summary-card">
              <span>📅 Date:</span>
              <b>{new Date(selectedDate).toLocaleDateString('en-IN', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'short' 
              })}</b>
            </div>
            <div className="summary-card">
              <span>🌾 Total Amount:</span>
              <b>₹{cattleFoodTotal.toFixed(2)}</b>
            </div>
            <div className="summary-card">
              <span>📝 Entries:</span>
              <b>{selectedDateCattleFood.length}</b>
            </div>
          </div>

          {/* Daily Cattle Food Entries */}
          <div className="card">
            <div className="table-header">
              <h3>Cattle Food for {new Date(selectedDate).toLocaleDateString('en-IN')}</h3>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Quantity</th>
                    <th>Rate (₹)</th>
                    <th>Total (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="table-loading">
                        <div className="mini-spinner"></div>Loading entries...
                      </td>
                    </tr>
                  ) : selectedDateCattleFood.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="empty-state">
                        No cattle food entries for this date.
                      </td>
                    </tr>
                  ) : (
                    selectedDateCattleFood.map((entry, index) => (
                      <tr key={`cattle-${entry.id}-${index}`}>
                        <td>{entry.itemName || 'N/A'}</td>
                        <td>{entry.quantity || 0}</td>
                        <td>₹{entry.rate || 0}</td>
                        <td>₹{(entry.totalAmount || 0).toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Cattle Food Entries */}
          <div className="card">
            <div className="table-header">
              <h3>Recent Cattle Food Entries</h3>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Item</th>
                    <th>Quantity</th>
                    <th>Rate (₹)</th>
                    <th>Total (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="table-loading">
                        <div className="mini-spinner"></div>Loading entries...
                      </td>
                    </tr>
                  ) : cattleFoodEntries.slice(0, 10).length === 0 ? (
                    <tr>
                      <td colSpan="5" className="empty-state">
                        No cattle food entries found.
                      </td>
                    </tr>
                  ) : (
                    cattleFoodEntries.slice(0, 10).map((entry, index) => (
                      <tr key={`recent-cattle-${entry.id}-${index}`}>
                        <td>{new Date(entry.date).toLocaleDateString('en-IN', { 
                          day: 'numeric', 
                          month: 'short' 
                        })}</td>
                        <td>{entry.itemName || 'N/A'}</td>
                        <td>{entry.quantity || 0}</td>
                        <td>₹{entry.rate || 0}</td>
                        <td>₹{(entry.totalAmount || 0).toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeView === 'payments' && (
        <>
          {/* Payment Summary */}
          <div className="summary-grid">
            <div className="summary-card">
              <span>💳 Total Payments:</span>
              <b>₹{totalPayments}</b>
            </div>
            <div className="summary-card">
              <span>📝 Payment Count:</span>
              <b>{payments.length}</b>
            </div>
            <div className="summary-card">
              <span>📅 Last Payment:</span>
              <b>{payments.length > 0 ? new Date(payments[0].createdAt?.toDate?.() || payments[0].createdAt).toLocaleDateString('en-IN') : 'N/A'}</b>
            </div>
          </div>

          {/* Payment History */}
          <div className="card">
            <div className="table-header">
              <h3>Payment History</h3>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount (₹)</th>
                    <th>Payment Method</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="table-loading">
                        <div className="mini-spinner"></div>Loading payments...
                      </td>
                    </tr>
                  ) : payments.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="empty-state">
                        No payments found.
                      </td>
                    </tr>
                  ) : (
                    payments.map((payment, index) => (
                      <tr key={`payment-${payment.id}-${index}`}>
                        <td>{new Date(payment.createdAt?.toDate?.() || payment.createdAt).toLocaleDateString('en-IN')}</td>
                        <td>₹{(payment.amount || 0).toFixed(2)}</td>
                        <td>{payment.paymentMethod || 'Cash'}</td>
                        <td>{payment.notes || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}